import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import ReactModal from "react-modal";

import { Table } from "..";
import { usePagination } from "../../hooks/usePagination";
import { useAppSelector } from "../../redux/hooks";
import { useGetFailedMovementsQuery } from "../../services";
import { toTitleCase } from "../../shared/Functions";
import { Session } from "../../types";
import {
  ColumnOneTableCellSkeleton,
  TableCellSkeleton,
} from "../Skeletons/Skeletons";
import { FilterInfo } from "../Table/TableFilters";

type SessionErrorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  session: Session | null;
  orgPlayerId?: string;
  failedMovementsCount?: number;
};

const columnHelper = createColumnHelper();

const FAILED_MOVEMENTS_PAGE_SIZE = 200;

// Error codes mapping: reasonForFailure description -> category
const ERROR_CODES = [
  { id: 0, description: "no error", category: "OK" },
  { id: 1, description: "total movement time too short", category: "Duration" },
  { id: 2, description: "total movement time too long", category: "Duration" },
  { id: 3, description: "no movement detected in file", category: "Duration" },
  { id: 4, description: "movement type not supported", category: "Type" },
  {
    id: 5,
    description: "shoulder width too variable, likely due to bad data",
    category: "Tracking",
  },
  { id: 6, description: "file has multiple movements", category: "Type" },
  { id: 7, description: "unknown", category: "Unknown" },
  { id: 8, description: "file did not pass QA", category: "Quality" },
  {
    id: 9,
    description:
      "key points not detected, norm_time is NaN, likely due to bad data",
    category: "Missing Data",
  },
  { id: 10, description: "player not in database", category: "Player" },
  { id: 11, description: "player weight unassigned", category: "Player" },
  { id: 12, description: "invalid weight", category: "Player" },
  { id: 13, description: "invalid height", category: "Player" },
  {
    id: 14,
    description: "No info available from official league API",
    category: "League",
  },
  { id: 15, description: "hawkeye elbow bug", category: "Tracking" },
  { id: 16, description: "opensim skeleton error", category: "Tracking" },
  {
    id: 17,
    description: "opensim inverse kinematics error",
    category: "Tracking",
  },
  {
    id: 18,
    description: "hawkeye lower half tracking error",
    category: "Tracking",
  },
  {
    id: 19,
    description: "incorrect handedness detection, likely due to bad data",
    category: "Player",
  },
  { id: 20, description: "mislabeled player", category: "Player" },
  {
    id: 21,
    description: "General motion tracking errors",
    category: "Tracking",
  },
  {
    id: 22,
    description: "KinaTrax entered weight of 0 into Visual3D",
    category: "Tracking",
  },
  {
    id: 23,
    description: "KinaTrax pitch_elbow_flex_range_norm is 0",
    category: "Tracking",
  },
  {
    id: 24,
    description: "time series discontinuity in underlying raw mocap data",
    category: "Missing Data",
  },
  { id: 25, description: "duplicate file or movement", category: "File" },
  {
    id: 26,
    description: "key points meaningless because no person was in camera frame",
    category: "Missing Data",
  },
  { id: 27, description: "incorrect movement type label", category: "Type" },
  { id: 28, description: "incorrect time step / fps", category: "File" },
  { id: 29, description: "incorrect units", category: "Missing Data" },
  { id: 30, description: "missing key points", category: "Missing Data" },
  {
    id: 31,
    description: "time steps not in sequential order",
    category: "Missing Data",
  },
  {
    id: 32,
    description: "problem with player or movement assignment in metadata",
    category: "Type",
  },
  {
    id: 33,
    description: "raw file path for movement does not exist",
    category: "File",
  },
  { id: 34, description: "bat tracking does not exist", category: "Tracking" },
  {
    id: 35,
    description: "inverse kinematics failed for unknown reason",
    category: "Tracking",
  },
  { id: 36, description: "pose estimation 2D failed", category: "Tracking" },
  { id: 37, description: "pose estimation 3D failed", category: "Tracking" },
  {
    id: 38,
    description: "out of the ordinary stats api request",
    category: "League",
  },
  {
    id: 39,
    description:
      "Could not analyze movement characteristics for any input movement in Analysis Station",
    category: "Unknown",
  },
  {
    id: 40,
    description: "no pose data available in raw file",
    category: "Missing Data",
  },
  {
    id: 41,
    description: "failed quality check before inverse-kinematics",
    category: "Quality",
  },
  { id: 42, description: "failed quality check generic", category: "Quality" },
  {
    id: 43,
    description: "failed quality check due to bat speed issue",
    category: "Quality",
  },
  {
    id: 44,
    description: "failed quality check due to max hand issue",
    category: "Quality",
  },
  {
    id: 45,
    description: "failed quality check due to shoulder width issue",
    category: "Quality",
  },
  {
    id: 46,
    description: "failed quality check due to issue with shoulders or hips",
    category: "Quality",
  },
  {
    id: 47,
    description:
      "failed quality check due to issue with Dom Arm / Leg or Opp Arm Leg",
    category: "Quality",
  },
  { id: 48, description: "incorrect video camera angle", category: "Camera" },
  {
    id: 49,
    description: "excessive change in segment lengths",
    category: "Tracking",
  },
  { id: 50, description: "player tagging issue", category: "Type" },
  { id: 51, description: "segment length ratio issue", category: "Tracking" },
  { id: 52, description: "unknown if Noah or NBA id", category: "League" },
  { id: 53, description: "invalid filename", category: "File" },
  { id: 54, description: "corrupt raw file path", category: "File" },
  { id: 55, description: "data migration issue", category: "Migration" },
];

// Helper function to get category from reasonForFailure
const getCategoryFromReason = (reasonForFailure: string): string => {
  if (!reasonForFailure) return "Unknown";

  const normalizedReason = reasonForFailure.toLowerCase().trim();
  const errorCode = ERROR_CODES.find(
    (code) => code.description.toLowerCase() === normalizedReason,
  );

  return errorCode?.category || "Unknown";
};

export const SessionErrorModal: React.FC<SessionErrorModalProps> = ({
  isOpen,
  onClose,
  session,
  orgPlayerId,
  failedMovementsCount: propFailedMovementsCount,
}) => {
  // #root only exists in the real app shell (index.html) — Cypress's
  // component-test harness mounts into a differently-identified root, so
  // skip there rather than letting react-modal throw on a missing selector.
  if (document.getElementById("root")) {
    ReactModal.setAppElement("#root");
  }

  const failedMovementsCount =
    propFailedMovementsCount ?? session?.failedMovements ?? 0;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const lastSessionIdRef = useRef<string | null>(null);

  // Get movement types from Redux for ID to name mapping
  const orgMovementTypes = useAppSelector(
    (state) => state.org.details.movementTypes,
  );
  // Get players from Redux for ID to name mapping
  const players = useAppSelector((state) => state.players.playerList);

  const failedMovementsQuery = useGetFailedMovementsQuery(
    {
      sessionId: session?.id || "",
      offset,
      limit: FAILED_MOVEMENTS_PAGE_SIZE,
      orgPlayerId: orgPlayerId,
    },
    {
      skip: !session || !isOpen || failedMovementsCount === 0,
    },
  );

  const {
    allData: failedMovements,
    isFetching: isLoading,
    resetPagination,
  } = usePagination(
    failedMovementsQuery,
    FAILED_MOVEMENTS_PAGE_SIZE,
    offset,
    setOffset,
    // Deduplicate by movementId (unique identifier for each failed movement)
    (item: any) => item.movementId,
  );

  const isError = failedMovementsQuery.isError;

  // Filter out Duration category errors from display
  const filteredFailedMovements = useMemo(() => {
    if (!failedMovements || failedMovements.length === 0) {
      return [];
    }
    return failedMovements.filter((movement: any) => {
      const reasonForFailure =
        movement.reasonForFailure || movement.reason_for_failure || "";
      const category = getCategoryFromReason(reasonForFailure);
      return category !== "Duration";
    });
  }, [failedMovements]);

  // Reset pagination when modal opens with a different session
  useEffect(() => {
    if (isOpen && session) {
      if (lastSessionIdRef.current !== session.id) {
        lastSessionIdRef.current = session.id;
        resetPagination();
      }
    } else if (!isOpen) {
      lastSessionIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, session?.id]);

  // Calculate error type breakdown by category
  const errorTypeBreakdown = useMemo(() => {
    if (!filteredFailedMovements || filteredFailedMovements.length === 0) {
      return {};
    }

    const breakdown: Record<string, number> = {};
    filteredFailedMovements.forEach((movement: any) => {
      const reasonForFailure =
        movement.reasonForFailure || movement.reason_for_failure || "";
      const category = getCategoryFromReason(reasonForFailure);
      breakdown[category] = (breakdown[category] || 0) + 1;
    });

    return breakdown;
  }, [filteredFailedMovements]);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    // Get unique players from failed movements
    let playerOptions: { value: string; label: string }[] = [
      { value: "", label: "All Players" },
    ];
    if (
      filteredFailedMovements &&
      filteredFailedMovements.length > 0 &&
      players.length > 0
    ) {
      const uniqueOrgPlayerIds = Array.from(
        new Set(
          filteredFailedMovements
            .map((m: any) => m.orgPlayerId)
            .filter((id: string) => id),
        ),
      );
      const playerOptionsList = uniqueOrgPlayerIds
        .map((playerId: string) => {
          const player = players.find((p: any) => p.orgPlayerId === playerId);
          if (!player) return null;
          return {
            value: `${player.firstName} ${player.lastName}`,
            label: `${player.firstName} ${player.lastName}`,
            lastName: player.lastName, // Keep lastName for sorting
          };
        })
        .filter(
          (item): item is { value: string; label: string; lastName: string } =>
            item !== null,
        );
      // Sort by last name
      playerOptionsList.sort((a, b) => a.lastName.localeCompare(b.lastName));
      playerOptions = [
        { value: "", label: "All Players" },
        ...playerOptionsList,
      ];
    }

    // Error type options from actual failed movements data
    let errorTypeOptions: { value: string; label: string }[] = [
      { value: "", label: "All Error Types" },
    ];
    if (filteredFailedMovements && filteredFailedMovements.length > 0) {
      const uniqueCategories = Array.from(
        new Set(
          filteredFailedMovements
            .map((m: any) => {
              const reasonForFailure =
                m.reasonForFailure || m.reason_for_failure || "";
              return getCategoryFromReason(reasonForFailure);
            })
            .filter(Boolean),
        ),
      ).sort();
      errorTypeOptions = [
        { value: "", label: "All Error Types" },
        ...uniqueCategories.map((category: string) => ({
          value: category,
          label: category,
        })),
      ];
    }

    // Extract movement types from actual failed movements data
    let movementTypeOptions: { value: string; label: string }[] = [
      { value: "", label: "All Movement Types" },
    ];
    if (filteredFailedMovements && filteredFailedMovements.length > 0) {
      const movementTypes = Array.from(
        new Set(
          filteredFailedMovements
            .map((m: any) => {
              const movementTypeId =
                m.movementTypeId ||
                m.movement?.id ||
                m.movement?.typeId ||
                m.movementType;
              const movementTypeName =
                m.movementType || m.movement?.type || m.movement?.name || "";

              // Map ID to name if we have an ID
              if (movementTypeId && !movementTypeName) {
                const movementType = orgMovementTypes.find(
                  (mt: any) =>
                    mt.id === movementTypeId ||
                    mt.id === Number(movementTypeId),
                );
                return movementType?.name || "";
              }
              return movementTypeName ? toTitleCase(movementTypeName) : "";
            })
            .filter(Boolean),
        ),
      );
      const movementTypeOptionsList = movementTypes.map((type: string) => ({
        value: type,
        label: type,
      }));
      movementTypeOptions = [
        { value: "", label: "All Movement Types" },
        ...movementTypeOptionsList,
      ];
    }

    return {
      players: playerOptions,
      movementTypes: movementTypeOptions,
      errorTypes: errorTypeOptions,
    };
  }, [filteredFailedMovements, orgMovementTypes, players]);

  const failedMovementsColumns = useMemo(
    () => [
      columnHelper.accessor(
        (row: any) => {
          // Get orgPlayerId from the failed movement
          const movementOrgPlayerId = row.orgPlayerId;
          if (!movementOrgPlayerId) return "";

          // Find the player in Redux store by orgPlayerId
          const player = players.find(
            (p: any) => p.orgPlayerId === movementOrgPlayerId,
          );
          if (!player) return "";

          // Format as "FirstName LastName"
          return `${player.firstName} ${player.lastName}`;
        },
        {
          id: "player",
          header: "Player",
          size: 250,
          cell: function playerCell(element: any) {
            const playerName = element.getValue() || "Player Name";
            return (
              <p data-testid="player-cell" className="text-gray-900">
                {playerName}
              </p>
            );
          },
        },
      ),
      columnHelper.accessor(
        (row: any) => {
          // Return formatted movement type for filtering
          const movementTypeId =
            row.movementTypeId ||
            row.movement?.id ||
            row.movement?.typeId ||
            row.movementType;
          const movementTypeName =
            row.movementType || row.movement?.type || row.movement?.name || "";

          // Map ID to name if we have an ID
          let displayName = movementTypeName;
          if (movementTypeId && !movementTypeName) {
            const movementType = orgMovementTypes.find(
              (mt: any) =>
                mt.id === movementTypeId || mt.id === Number(movementTypeId),
            );
            displayName = movementType?.name || "";
          }

          return displayName ? toTitleCase(displayName) : "";
        },
        {
          id: "movementType",
          header: "Movement Type",
          size: 200,
          cell: function movementTypeCell(element: any) {
            const displayName = element.getValue();
            return (
              <p data-testid="movement-type-cell" className="text-gray-900">
                {displayName || ""}
              </p>
            );
          },
        },
      ),
      columnHelper.accessor(
        (row: any) => {
          // Return category from reasonForFailure for filtering
          const reasonForFailure =
            row.reasonForFailure || row.reason_for_failure || "";
          return {
            category: getCategoryFromReason(reasonForFailure),
            description: reasonForFailure,
          };
        },
        {
          id: "error",
          header: "Error Type",
          size: 200,
          sortingFn: (rowA: any, rowB: any) => {
            const catA = rowA.getValue("error")?.category || "";
            const catB = rowB.getValue("error")?.category || "";
            return catA.localeCompare(catB);
          },
          filterFn: (row: any, columnId: string, filterValue: string) => {
            const value = row.getValue(columnId);
            if (!filterValue) return true;
            return value?.category === filterValue;
          },
          cell: function errorCell(element: any) {
            const { category, description } = element.getValue() || {};
            // Category-specific colors
            const categoryColors: Record<string, string> = {
              Camera: "bg-purple-100 text-purple-700",
              File: "bg-yellow-100 text-gray-700",
              League: "bg-indigo-100 text-indigo-700",
              Migration: "bg-pink-100 text-pink-700",
              "Missing Data": "bg-red-100 text-red-700",
              Player: "bg-green-100 text-green-700",
              Quality: "bg-orange-100 text-orange-700",
              Tracking: "bg-blue-100 text-blue-700",
              Type: "bg-cyan-100 text-cyan-700",
              Unknown: "bg-gray-200 text-gray-600",
            };
            const colorClass =
              categoryColors[category] || categoryColors["Unknown"];
            return (
              <div className="relative group">
                <span
                  data-testid="error-cell"
                  className={`inline-flex items-center justify-center px-3 py-1 rounded-lg text-sm font-medium cursor-default ${colorClass}`}
                >
                  {category}
                </span>
                {description && (
                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none">
                    <div className="bg-white text-gray-700 text-xs rounded-lg py-2 px-3 max-w-xs shadow-lg border border-gray-200 whitespace-normal">
                      {description}
                    </div>
                    <div className="absolute left-4 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-white drop-shadow-sm" />
                  </div>
                )}
              </div>
            );
          },
        },
      ),
      columnHelper.accessor("learnMore", {
        header: "",
        enableSorting: false,
        size: 150,
        cell: function learnMoreCell() {
          return (
            <div data-testid="learn-more-cell">
              <a
                href="https://docs.rebootmotion.com/"
                className="text-gray-900 hover:text-gray-700 flex items-center gap-1 underline"
                data-testid="learn-more-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn More
                <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-900 stroke-2 ml-1" />
              </a>
            </div>
          );
        },
      }),
    ],
    [orgMovementTypes, players],
  );

  const tableData = useMemo(
    () =>
      isLoading && filteredFailedMovements.length === 0
        ? Array(5).fill({})
        : filteredFailedMovements || [],
    [filteredFailedMovements, isLoading],
  );

  const isInitialLoad = isLoading && filteredFailedMovements.length === 0;
  const skeletonColumns = useMemo(
    () =>
      isInitialLoad
        ? failedMovementsColumns.map((column, index) => ({
            ...column,
            cell:
              index === 0 ? (
                <ColumnOneTableCellSkeleton />
              ) : (
                <TableCellSkeleton />
              ),
          }))
        : failedMovementsColumns,
    [isInitialLoad, failedMovementsColumns],
  );

  const filters: FilterInfo[] = useMemo(
    () => [
      {
        type: "dropdown",
        placeholder: "Player",
        filterOptions: filterOptions.players,
        columnId: "player",
      },
      {
        type: "dropdown",
        placeholder: "Movement Type",
        filterOptions: filterOptions.movementTypes,
        columnId: "movementType",
      },
      {
        type: "dropdown",
        placeholder: "Error Type",
        filterOptions: filterOptions.errorTypes,
        columnId: "error",
      },
    ],
    [filterOptions],
  );

  const screenWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
  const isSmallScreen = isMobile || screenWidth < 1024;

  const customStyles = {
    overlay: {
      position: "fixed" as const,
      zIndex: 9999,
      height: "100vh",
      width: "100vw",
      top: "0px",
      left: "0px",
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "stretch",
      transitionProperty: "all",
      transitionTimingFunction: "ease-in-out",
      transitionDuration: "0.3s",
      border: "none",
    },
    content: {
      fontFamily: "var(--font-body)",
      border: "none",
      borderRadius: "0",
      padding: "0",
      margin: "0",
      height: "100vh",
      width: isSmallScreen ? "100%" : "50%",
      maxWidth: isSmallScreen ? "100%" : "50%",
      position: "fixed" as const,
      top: "0px",
      left: "auto",
      right: "0px",
      bottom: "0px",
      background: "#fff",
      overflow: "auto",
      WebkitOverflowScrolling: "touch" as const,
      outline: "none",
      boxShadow: "-4px 0 6px rgba(0, 0, 0, 0.1)",
    },
  };

  return (
    <ReactModal isOpen={isOpen} style={customStyles} onRequestClose={onClose}>
      <div className="flex flex-col h-full">
        <div
          className="w-full bg-white border-b border-gray-200 flex justify-between items-start px-6 py-4 sticky top-0 z-50"
          data-testid="session-error-modal-header"
        >
          <div className="flex flex-col">
            <h2
              className="text-xl font-medium text-gray-900"
              data-testid="session-error-modal-title"
            >
              Session: {session?.id || ""} - Error
            </h2>
            <h2
              className="text-xl font-medium text-gray-900"
              data-testid="session-error-modal-summary"
            >
              <span className="font-medium">Summary:</span>{" "}
              <span className="font-normal text-gray-700 text-base">
                {failedMovementsCount} total errors
                {!isLoading &&
                  Object.keys(errorTypeBreakdown).length > 0 &&
                  ": "}
                {!isLoading &&
                  (() => {
                    const sortedCategories = Object.entries(
                      errorTypeBreakdown,
                    ).sort(([, a], [, b]) => b - a);
                    const displayCategories = sortedCategories.slice(0, 3);
                    const remainingCount = sortedCategories.length - 3;

                    return (
                      <>
                        {displayCategories.map(
                          ([category, count], index, arr) => (
                            <span key={category}>
                              {count} {category}
                              {index < arr.length - 1 && ", "}
                            </span>
                          ),
                        )}
                        {remainingCount > 0 && (
                          <span className="text-gray-500">
                            {" "}
                            +{remainingCount} more
                          </span>
                        )}
                      </>
                    );
                  })()}
              </span>
            </h2>
          </div>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            data-testid="session-error-modal-close-button"
          >
            <XMarkIcon className="h-6 w-6 stroke-2" />
          </button>
        </div>
        <div ref={scrollContainerRef} className="flex-1 overflow-auto py-4">
          {isError ? (
            <div
              className="flex flex-col items-center justify-center py-16 text-gray-500"
              data-testid="error-state"
            >
              <svg
                className="h-12 w-12 text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-lg font-medium text-gray-700">
                Unable to load error details
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Please try again later
              </p>
            </div>
          ) : (isLoading && filteredFailedMovements.length === 0) ||
            (filteredFailedMovements && filteredFailedMovements.length > 0) ? (
            <div data-testid="failed-movements-table">
              <Table
                key={`table-${tableData.length}`}
                columns={skeletonColumns}
                data={tableData}
                selectedPageSize={String(tableData.length || 50)}
                displayFooter={false}
                fetching={isLoading && filteredFailedMovements.length === 0}
                filters={filters}
                hideSearch={true}
              />
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center py-16 text-gray-500"
              data-testid="empty-state"
            >
              <svg
                className="h-12 w-12 text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-lg font-medium text-gray-700">
                No error details available
              </p>
            </div>
          )}
        </div>
      </div>
    </ReactModal>
  );
};
